/**
 * A step glides to every place it is given without being dragged: the layout
 * after a step grew or one was added, undo, a collaborator, the gap a step
 * dragged from the library would need. The stored box is final at once; only
 * the painted box (`transientXYWH`, local and never stored) moves there, so
 * the connectors glide along.
 */
import {
  isStreamLainMotionEnabled,
  STREAMLAIN_MOTION,
  streamLainMotionProgress,
} from '@blocksuite/affine-shared/utils';
import { Bound, type SerializedXYWH } from '@blocksuite/global/gfx';

export interface StepGlideModel {
  readonly xywh: SerializedXYWH;
  transientXYWH: SerializedXYWH | undefined;
}

export interface StepGlideInput {
  readonly model: StepGlideModel;
  readonly view: Window;
  /** Paints the block and its connectors after the painted box changed. */
  place(): void;
}

const progressOf = streamLainMotionProgress('morph');

export class StepGlide {
  /** The stored box the painted box last went to. */
  private _stored: SerializedXYWH;
  /** A box shown in place of the stored one, e.g. the room for a dragged step. */
  private _target: SerializedXYWH | undefined;
  /** The painted box this glide set; another one belongs to somebody else. */
  private _painted: SerializedXYWH | undefined;
  private _frame: number | undefined;
  private _held = false;

  constructor(private readonly input: StepGlideInput) {
    this._stored = input.model.xywh;
  }

  /** A drag moves the box itself; it is never glided. */
  hold(): void {
    this._stop();
    this._held = true;
  }

  release(): void {
    this._held = false;
    this._stored = this.input.model.xywh;
  }

  /** Every change of the box, stored or painted, comes here. */
  changed(): void {
    const stored = this.input.model.xywh;
    if (stored === this._stored) return;
    const from = this._painted ?? this._stored;
    this._stored = stored;
    if (this._held || this._foreign()) {
      this._stop();
      return;
    }
    this._moveFrom(from);
  }

  /** Shows `target` instead of the stored box; undefined goes back to it. */
  show(target: SerializedXYWH | undefined): void {
    if (target === this._target) return;
    this._target = target;
    if (this._held || this._foreign()) return;
    this._moveFrom(this._painted ?? this._stored);
  }

  dispose(): void {
    this._stop();
  }

  private _foreign(): boolean {
    const { transientXYWH } = this.input.model;
    return transientXYWH !== undefined && transientXYWH !== this._painted;
  }

  private _reduced(): boolean {
    return (
      !isStreamLainMotionEnabled('canvas') ||
      this.input.view.matchMedia('(prefers-reduced-motion: reduce)').matches
    );
  }

  private _paint(xywh: SerializedXYWH | undefined): void {
    if (xywh === this._painted && xywh === this.input.model.transientXYWH) {
      return;
    }
    this._painted = xywh;
    this.input.model.transientXYWH = xywh;
    this.input.place();
  }

  private _cancelFrame(): void {
    if (this._frame !== undefined) {
      this.input.view.cancelAnimationFrame(this._frame);
    }
    this._frame = undefined;
  }

  /** Ends the glide; the stored box shows unless somebody else paints one. */
  private _stop(): void {
    this._cancelFrame();
    if (
      this._painted !== undefined &&
      this.input.model.transientXYWH === this._painted
    ) {
      this._paint(undefined);
    }
    this._painted = undefined;
  }

  /** The glide arrived: the target stays painted, the stored box needs none. */
  private _settle(): void {
    this._cancelFrame();
    if (this._target !== undefined) this._paint(this._target);
    else if (this._painted !== undefined) this._paint(undefined);
  }

  private _moveFrom(fromXYWH: SerializedXYWH): void {
    const { model, view } = this.input;
    this._cancelFrame();
    const toXYWH = this._target ?? this._stored;
    if (fromXYWH === toXYWH || this._reduced()) {
      this._settle();
      return;
    }
    const from = Bound.deserialize(fromXYWH);
    const to = Bound.deserialize(toXYWH);
    const start = view.performance.now();
    const duration = STREAMLAIN_MOTION.morph.duration;
    this._paint(fromXYWH);
    const tick = (now: number) => {
      this._frame = undefined;
      // A drag or a collaborator's preview took the painted box over.
      if (model.transientXYWH !== this._painted) {
        this._painted = undefined;
        return;
      }
      const progress = Math.min(1, Math.max(0, (now - start) / duration));
      if (progress === 1 || this._reduced()) {
        this._settle();
        return;
      }
      const eased = progressOf(progress);
      this._paint(
        new Bound(
          from.x + (to.x - from.x) * eased,
          from.y + (to.y - from.y) * eased,
          from.w + (to.w - from.w) * eased,
          from.h + (to.h - from.h) * eased
        ).serialize()
      );
      this._frame = view.requestAnimationFrame(tick);
    };
    this._frame = view.requestAnimationFrame(tick);
  }
}
